import _ from 'lodash'
import i18next from 'i18next'
import constants from '@/constants'
import { getFullName } from '@/lib/user'
import { getRevCurrency } from '@/lib/rev'
import currencies from '@/locales/currencies.json'
import { formatDate } from '@/lib/date'

function revCurrency(rev) {
  return getRevCurrency(rev) || ''
}

function currencySymbol(rev) {
  return _.get(currencies[revCurrency(rev)], 'symbol_native', '')
}

function totalImpactText(rev) {
  const totalImpact = rev?.validatedReview?.totalImpact ?? null
  const value = totalImpact
    ? Number(totalImpact.toLocaleString(rev?.lang))
    : '-'

  return `${currencySymbol(rev)} ${value} ${revCurrency(rev)}`
}
function startDate(rev) {
  return rev?.impactStartsAt
    ? formatDate(rev?.impactStartsAt, 'MMM d, yy')
    : null
}

function endDate(rev) {
  return rev?.impactEndsAt ? formatDate(rev?.impactEndsAt, 'MMM d, yy') : null
}

function impactTimeFrameText(rev) {
  return `${startDate(rev)} ${i18next.t('common.to')} ${endDate(rev)}`
}

function getStatusTranslationKey(status) {
  return _.snakeCase(status)
}

function revStatusText(rev) {
  return i18next.t(
    `common.rev_condition.${getStatusTranslationKey(rev?.status)}`
  )
}

export default function parseRev(rev) {
  const coach = rev?.coach
  const author = rev?.author
  const collaborators = rev?.collaborators

  const defaultData = {
    name: rev?.name ?? '',
    id: rev?.id ?? '',
    entityType: rev?.entityType,
    tags: rev?.tags,
    coach: rev.coach ? getFullName(coach) : null,
    coachObject: rev?.coach,
    ownerObject: rev?.owner,
    author: rev.author ? getFullName(author) : null,
    authorObject: rev?.author,
    team: rev?.collaborators
      ? collaborators.map(collaborator => getFullName(collaborator)).join(' | ')
      : null,
    categories: rev?.categories
      ? rev?.categories?.map(c => i18next.t(`categories.${c}`)).join(' | ')
      : null,
    teamObject: rev?.collaborators,
    site: rev?.site?.name,
    country: i18next.t(`countries.${rev?.countryCodes[0]}`),
    status: revStatusText(rev),
    statusId: rev?.status,
    totalImpact: totalImpactText(rev),
    impactTimeFrame: impactTimeFrameText(rev),
    impactBenefits: rev?.validatedReview?.estimationExplanation ?? '',
    emptyImpactBenefits: i18next.t('rev_detail.empty_impact_benefits'),
    createdAt: formatDate(rev?.createdAt, 'MMM d, yy'),
    completedAt: formatDate(rev?.completedAt, 'MMM d, yy'),
    implementedAt: formatDate(
      rev?.validations?.implementation?.validatedAt,
      'MMM d, yy'
    ),
    impactValidatedAt: formatDate(
      rev?.validations?.financial?.validatedAt,
      'MMM d, yy'
    ),
    validations: rev?.validations,
  }
  switch (rev.entityType) {
    case constants.revTypes.improvementIdea:
      return {
        ...defaultData,
        problem: rev?.relate?.problem ?? '',
        solution: rev?.expand?.solution ?? '',
        solutionMedia: !_.isEmpty(rev?.expand?.media)
          ? rev?.expand?.media
          : null,
        revMedia: !_.isEmpty(rev?.relate?.media) ? rev?.relate?.media : null,
        revType: rev?.entityType,
      }
    case constants.revTypes.problemSolving:
      return {
        ...defaultData,
        problem: rev?.problemAnalysis?.problem ?? '',
        solution: rev?.solutionDescription ?? '',
        solutionMedia: !_.isEmpty(rev?.solutionMedia)
          ? rev?.solutionMedia
          : null,
        revMedia: !_.isEmpty(rev?.problemAnalysis?.problemMedia)
          ? rev?.problemAnalysis?.problemMedia
          : null,
        revType: rev?.entityType,
      }
    default:
      return {
        ...defaultData,
        problem: rev?.beforePhase?.situation ?? '',
        solution: rev?.afterPhase?.solution ?? '',
        solutionMedia: !_.isEmpty(rev?.afterPhase?.media)
          ? rev?.afterPhase?.media
          : null,
        revMedia: !_.isEmpty(rev?.beforePhase?.media)
          ? rev?.beforePhase?.media
          : null,
        revType: rev?.entityType,
      }
  }
}
